import { Box, Flex, Text, VStack, HStack } from "@chakra-ui/react";
import { StyledDashboardLayoutChildContainer } from "@root/components/common/style";
import useProjectsDashboard from "@root/hooks/useProjectsDashboard";
import { USER_DASHBOARD_INPUT_NAMES, WEB_PROJECTS } from "@root/webConstants";
import { AI, AI_MODEL_OPTIONS, CHAT_MODEL_TYPE, CHAT_STATUS, USER } from "@root/constants";
import { useDispatch, useSelector } from "react-redux";
import { ChatHeader } from "@root/components/chat/ChatHeader";
import { ChatTypingIndicator } from "@root/components/chat/ChatTypingIndicator";
import { CHAT_COLUMN_MAX_W } from "@root/components/chat/chatStyles";
import { useEffect, useRef } from "react";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useCountryContext } from "@root/context/use-country-context";
import {
  LENDING_OPENED,
  reportDashboardLending,
} from "@root/reporting/dashboard-events";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useTranslation } from "react-i18next";
import { SmallText } from "@root/components/common/text/SmallText";
import { useLocation } from "react-router-dom";
import { useProjectsContext } from "@root/context/projects-context";
import { ChatBotInput } from "./ChatBotInput";
import { MultiAIChatMessage } from "@root/components/MultiAskAIChatbot/MultiAIChatMessage";
import SuggestedQueryChips from "@root/pages/dashboard/components/SuggestedQueryChips";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import { getCurrentLanguage } from "@root/store/selectors/features";
import { useChatScroll } from "@root/hooks/useChatScroll";
import { ScrollToBottomButton } from "@root/components/common/ScrollToBottomButton";

export const AskAIChatbot = () => {
  const dispatch = useDispatch();
  const {
    state,
    inputChangeHandler,
    onSubmitHandler,
    multiAIMessages,
    isMultiAIPolling,
    stopPolling,
    currentStatus,
    completedMessageIds,
    restoreMultiAIThread,
    openMultiAIThread,
    resetMultiAIChat,
    multiAIThreadId,
    multiAIChatType,
  } = useProjectsDashboard();
  const { fetchSuggestedQuestions, suggestedQuestions, setSelectedAskAiModel } =
    useRiskAssessmentContext();
  // Spinner visibility and status text control
  const lastMessage = multiAIMessages?.[multiAIMessages?.length - 1];
  const hasAIContent =
    lastMessage?.type === AI && Boolean(lastMessage?.content?.trim?.()?.length);
  const shouldShowSpinner = Boolean(isMultiAIPolling);
  const { country, isLoading, currentLanguage, OS, browser } =
    useCountryContext();
  const currentUser: any = useSelector(getLoggedInUser);
  const { t } = useTranslation("common");
  // const navigate = useNavigate();
  const { pathname } = useLocation();

  const { setCurrentPathname } = useMainChatContext();

  const { fetchProjects, projectsData } = useProjectsContext();

  // Multi-AI Chat state
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Use the scroll hook for all scroll-related functionality
  const {
    chatMessagesRef,
    messagesEndRef,
    showScrollToBottomButton,
    handleScrollToBottomClick,
  } = useChatScroll({
    messages: multiAIMessages || [],
    isPolling: isMultiAIPolling,
  });

  // Reload the current conversation after a page refresh
  useEffect(() => {
    restoreMultiAIThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && country) {
      const params: any = {
        currentLanguage,
        country_name: country?.country_name,
        country_code: country?.country_code,
        city: country?.city,
        state: country?.state,
        OS: OS,
        userId: currentUser ? currentUser?.id : null,
        userEmail: currentUser ? currentUser?.email : "Anonymous",
        regMode: currentUser ? currentUser?.regMode : "Anonymous",
        eventName: LENDING_OPENED,
        ip_address: country?.IPv4,
        user_agent: browser,
      };
      reportDashboardLending(params, dispatch);
    }
  }, [isLoading, country]);

  useEffect(() => {
    if (!projectsData?.rows?.length) {
      fetchProjects();
    }
  }, [fetchProjects, projectsData?.rows?.length]);

  const currentLanguageApp = useSelector(getCurrentLanguage);

  useEffect(() => {
    // Reset AskAI model selection on mount
    setSelectedAskAiModel?.(null);
    fetchSuggestedQuestions({
      moduleType: CHAT_MODEL_TYPE.ASK_AI,
      modelType: 1,
    });
  }, []);

  useEffect(() => {
    if (multiAIMessages?.length) return;
    fetchSuggestedQuestions({
      moduleType: CHAT_MODEL_TYPE.ASK_AI,
      modelType: 1,
    });
  }, [currentLanguageApp]);

  useEffect(() => {
    return () => {
      // Reset AskAI model selection on unmount
      setSelectedAskAiModel(null);
    };
  }, []);

  useEffect(() => {
    setCurrentPathname(pathname);
  }, [pathname]);

  const renderSuggestedQuestions = () => {
    return (
      <SuggestedQueryChips
        suggestedQuestions={suggestedQuestions}
        onSubmitHandler={onSubmitHandler}
      />
    );
  };

  const renderMainLayout = () => {
    const hasMessages = multiAIMessages?.length > 0;

    return (
      <Flex w="100%" h="100%" direction="column" position="relative" overflow="hidden">
        <ChatHeader
          title={t("translate.letsExploreToday")}
          chatType={multiAIChatType}
          activeThreadId={multiAIThreadId}
          onNewChat={resetMultiAIChat}
          onOpenThread={openMultiAIThread}
        />
        <StyledDashboardLayoutChildContainer
          flex="1"
          minH={0}
          overflow="hidden"
          px={{ base: "16px", lg: "32px" }}
        >
          {!hasMessages ? (
            /* No messages - centered layout */
            <Flex
              w="100%"
              h="100%"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              gap="40px"
            >
              <Flex
                maxW="800px"
                minW={"500px"}
                flexDir={"column"}
                gap={"10px"}
                borderRadius="16px"
                justifyContent="center"
                alignItems="center"
                p={"16px"}
              >
                <Text
                  bgClip="text"
                  textColor="#005EB8"
                  fontWeight="600"
                  fontSize="56px"
                  textAlign="center"
                  lineHeight="1.2"
                >
                  {t("translate.letsExploreToday")}
                </Text>
                <SmallText
                  color="#010610"
                  fontSize={"20px"}
                  text={t("translate.askFromDoosanAI")}
                />
              </Flex>

              {/* Suggested Query Chips */}
              {renderSuggestedQuestions()}

              <Box w="100%" maxW={CHAT_COLUMN_MAX_W}>
                <ChatBotInput
                  maxW={CHAT_COLUMN_MAX_W}
                  pid={WEB_PROJECTS.KSCE_GPT.id}
                  noOfDocs={0}
                  onSubmit={() => null}
                  fileIndex={0}
                  documents={[]}
                  onChangehandler={inputChangeHandler}
                  input={state.doazGptInput}
                  submitHandler={onSubmitHandler}
                  loading={isMultiAIPolling}
                  width="100%"
                  m="0px"
                  name={USER_DASHBOARD_INPUT_NAMES.DOAZGPT}
                  stopPolling={stopPolling}
                  currentStatus={currentStatus}
                />
              </Box>
            </Flex>
          ) : (
            /* Has messages - sticky input with scroll container */
            <Flex w="100%" flex="1" minH={0} flexDirection="column" position="relative">
              {/* Scrollable Messages Area */}
              <Box
                ref={chatMessagesRef}
                flex="1"
                overflowY="auto"
                overflowX="hidden"
                position="relative"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                }}
              >
                <VStack
                  spacing={0}
                  align="stretch"
                  maxW={CHAT_COLUMN_MAX_W}
                  mx="auto"
                  pt="28px"
                  px="4px"
                >
                  {multiAIMessages?.map((message: any, index: number) => {
                    const isLast = index === multiAIMessages?.length - 1;
                    const shouldInsertBeforeAI =
                      shouldShowSpinner && isLast && message?.type === AI;
                    const shouldInsertAfterUser =
                      shouldShowSpinner && isLast && message?.type === USER;
                    return (
                      <>
                        <MultiAIChatMessage
                          key={message?.id}
                          showFollowUps={isLast}
                          message={message}
                          aiModelOptions={AI_MODEL_OPTIONS as any}
                          showActions={true}
                          currentStatus={currentStatus}
                          completedMessageIds={completedMessageIds}
                          onSubmitHandler={onSubmitHandler}
                          isStreaming={
                            isMultiAIPolling && isLast && message?.type === AI
                          }
                        />
                        {shouldInsertAfterUser && (
                          <ChatTypingIndicator
                            key={`status-after-${message?.id}`}
                            label={`${t("doosanAI.title")} ${t("doosanAI.thinking")}`}
                          />
                        )}
                        {isLast &&
                          message?.type === AI &&
                          isMultiAIPolling &&
                          (currentStatus === CHAT_STATUS.FOLLOW_UP ||
                            currentStatus === CHAT_STATUS.SOURCE) && (
                            <ChatTypingIndicator
                              key={`sources-${message?.id}`}
                              label={t("chatThreads.addingSources")}
                            />
                          )}
                      </>
                    );
                  })}
                  {/* Scroll anchor at the end */}
                  <Box ref={messagesEndRef} h="1px" />
                </VStack>
              </Box>

              {/* Scroll to Bottom Button - Fixed above input */}
              <ScrollToBottomButton
                show={showScrollToBottomButton}
                onClick={handleScrollToBottomClick}
              />

              {/* Sticky Input at Bottom */}
              <Box
                ref={inputContainerRef}
                position="sticky"
                bottom="0"
                w="100%"
                py="0px"
                bg="white"
                zIndex={100}
                mt="auto"
              >
                <Flex justify="center" w="100%" px="20px">
                  <Box w="100%" maxW={CHAT_COLUMN_MAX_W}>
                    <ChatBotInput
                      maxW={CHAT_COLUMN_MAX_W}
                      pid={WEB_PROJECTS.KSCE_GPT.id}
                      noOfDocs={0}
                      onSubmit={() => null}
                      fileIndex={0}
                      documents={[]}
                      onChangehandler={inputChangeHandler}
                      input={state.doazGptInput}
                      submitHandler={onSubmitHandler}
                      loading={isMultiAIPolling}
                      width="100%"
                      m="0px"
                      name={USER_DASHBOARD_INPUT_NAMES.DOAZGPT}
                      stopPolling={stopPolling}
                      currentStatus={currentStatus}
                    />
                  </Box>
                </Flex>
              </Box>
            </Flex>
          )}
        </StyledDashboardLayoutChildContainer>
      </Flex>
    );
  };

  const renderContent = () => {
    return renderMainLayout();
  };

  return renderContent();
};
